import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import DispatchClients from './DispatchClients';
import ObjectifsKPIs from './ObjectifsKPIs';
import CarteClients from './CarteClients';
import Rapports from './Rapports';
import RechercheGlobale from './RechercheGlobale';
import FicheCommercial from './FicheCommercial';
import SelecteurTheme from './SelecteurTheme';
import Notifications from './Notifications';
import { useTheme } from '../ThemeContext';
import PlanningSemaine from './PlanningSemaine';
import GestionCommerciaux from './GestionCommerciaux';
import { useLang, LangToggle } from '../LanguageContext';
import TrackingGPS from './TrackingGPS';
import { t } from '../i18n';
import ListeClientsManager from './ListeClientsManager';
import GestionProduits from './GestionProduits';
import HistoriqueActions from './HistoriqueActions';
import Commandes from './Commandes';

export default function ManagerHome({ user, onLogout }) {
  const { theme } = useTheme();
  const { lang, isRTL } = useLang();
  const [showTheme, setShowTheme] = useState(false);
  const [showRecherche, setShowRecherche] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [nbNotifs, setNbNotifs] = useState(0);
  const [ficheCommercial, setFicheCommercial] = useState(null);
  const [commerciaux, setCommerciaux] = useState([]);
  const [visites, setVisites] = useState([]);
  const [clients, setClients] = useState([]);
  const [actions, setActions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [vue, setVue] = useState('dashboard');

  useEffect(() => { charger(); chargerNbNotifs(); }, []);

  const chargerNbNotifs = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'notifications'), where('destinataireId', '==', user.uid), where('lu', '==', false)));
      setNbNotifs(snap.size);
    } catch(e) {}
  };

  const charger = async () => {
    try {
      const cSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      setCommerciaux(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const vSnap = await getDocs(collection(db, 'visites'));
      setVisites(vSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const clSnap = await getDocs(collection(db, 'clients'));
      setClients(clSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const aSnap = await getDocs(query(collection(db, 'actions'), where('statut', '==', 'A FAIRE')));
      setActions(aSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log('Erreur:', e); }
  };

  const handleLogout = async () => { await signOut(auth); onLogout(); };

  if (vue === 'dispatch') return <DispatchClients onClose={() => { setVue('dashboard'); charger(); }} />;
  if (vue === 'kpis') return <ObjectifsKPIs onClose={() => setVue('dashboard')} />;
  if (vue === 'carte') return <CarteClients isManager={true} user={user} onClose={() => setVue('dashboard')} />;
  if (vue === 'rapports') return <Rapports onClose={() => setVue('dashboard')} />;
  if (showRecherche) return <RechercheGlobale user={user} isManager={true} onClose={() => setShowRecherche(false)} onSelectClient={(c) => { setShowRecherche(false); }} onSelectCommercial={(c) => { setShowRecherche(false); setFicheCommercial(c); }} />;
  if (showNotifs) return <Notifications user={user} isManager={true} onClose={() => { setShowNotifs(false); chargerNbNotifs(); }} />;
  if (ficheCommercial) return <FicheCommercial commercial={ficheCommercial} onClose={() => { setFicheCommercial(null); charger(); }} />;
  if (vue === 'planning') return <PlanningSemaine isManager={true} user={user} onClose={() => setVue('dashboard')} />;
  if (vue === 'gestion') return <GestionCommerciaux onClose={() => { setVue('dashboard'); charger(); }} />;
  if (vue === 'tracking') return <TrackingGPS onClose={() => setVue('dashboard')} />;
  if (vue === 'clients') return <ListeClientsManager onClose={() => { setVue('dashboard'); charger(); }} />;
  if (vue === 'produits') return <GestionProduits onClose={() => setVue('dashboard')} />;
  if (vue === 'historique') return <HistoriqueActions onClose={() => setVue('dashboard')} />;
  if (vue === 'commandes') return <Commandes onClose={() => setVue('dashboard')} />;

  const today = new Date().toLocaleDateString(lang === 'ar' ? 'ar-DZ' : 'fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const visitesToday = visites.filter(v => v.heureDebut && new Date(v.heureDebut).toDateString() === new Date().toDateString());
  const actionsRetard = actions.filter(a => a.dateAction && new Date(a.dateAction) < new Date());
  const clientsNonAssignes = clients.filter(c => !c.commercialIds || c.commercialIds.length === 0);

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...styles.topbar, background: theme.couleur }}>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarName}>{t(lang, 'dashboard_manager')}</div>
          <div style={styles.topbarSub}>{t(lang, 'hello')}, {user.prenom || user.nom} · {today}</div>
        </div>
        <LangToggle />
        <button style={topbarBtn} onClick={() => setShowRecherche(true)}>🔍</button>
        <button style={{ ...topbarBtn, position: 'relative' }} onClick={() => setShowNotifs(true)}>
          🔔{nbNotifs > 0 && <span style={badgeStyle}>{nbNotifs}</span>}
        </button>
        <button style={topbarBtn} onClick={() => setShowTheme(true)}>🎨</button>
        <button onClick={handleLogout} style={styles.logoutBtn}>⬛</button>
      </div>

      <div style={styles.content}>
        {/* STATS */}
        <div style={styles.statsGrid}>
          {[
            { val: visites.length, label: t(lang, 'visites_total'), color: '#1C2833', sub: `${visitesToday.length} ${t(lang, 'visites_today')}` },
            { val: commerciaux.length, label: t(lang, 'commerciaux'), color: '#3B6D11', sub: t(lang, 'non_assignes') },
            { val: actionsRetard.length, label: t(lang, 'actions_retard'), color: '#C0392B', sub: '' },
            { val: clients.length, label: t(lang, 'clients_total'), color: '#185FA5', sub: `${clientsNonAssignes.length} ${t(lang, 'non_assignes')}` },
          ].map((s, i) => (
            <div key={i} style={styles.statCard}>
              <div style={{ ...styles.statVal, color: s.color }}>{s.val}</div>
              <div style={styles.statLabel}>{s.label}</div>
              <div style={styles.statSub}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* ALERTE */}
        {clientsNonAssignes.length > 0 && (
          <div style={styles.alertCard} onClick={() => setVue('dispatch')}>
            <div style={styles.alertIcon}>⚠️</div>
            <div style={{ flex: 1 }}>
              <div style={styles.alertTitle}>{clientsNonAssignes.length} {t(lang, 'alert_non_assignes')}</div>
              <div style={styles.alertSub}>{t(lang, 'assign_click')}</div>
            </div>
            <span style={styles.alertArrow}>{isRTL ? '←' : '→'}</span>
          </div>
        )}

        {/* ACTIONS RAPIDES */}
        <div style={styles.sectionLabel}>{t(lang, 'quick_actions')}</div>
        <div style={styles.actionsGrid}>
          {[
            { icon: '👥', label: t(lang, 'dispatch_clients'), action: () => setVue('dispatch') },
            { icon: '🎯', label: t(lang, 'objectifs_kpis'), action: () => setVue('kpis') },
            { icon: '📅', label: t(lang, 'plannings'), action: () => setVue('planning') },
            { icon: '👤', label: t(lang, 'gestion_commerciaux'), action: () => setVue('gestion') },
            { icon: '📊', label: t(lang, 'rapports'), action: () => setVue('rapports') },
            { icon: '🗺️', label: t(lang, 'carte_title'), action: () => setVue('carte') },
            { icon: '📡', label: 'Tracking GPS', action: () => setVue('tracking') },
            { icon: '📋', label: 'Liste Clients', action: () => setVue('clients') },
            { icon: '🛍️', label: 'Produits', action: () => setVue('produits') },
            { icon: '🛒', label: 'Commandes & CA', action: () => setVue('commandes') },
            { icon: '📋', label: 'Historique Actions', action: () => setVue('historique') },
          ].map((a, i) => (
            <button key={i} style={styles.actionCard} onClick={a.action}>
              <div style={styles.actionIcon}>{a.icon}</div>
              <div style={styles.actionLabel}>{a.label}</div>
            </button>
          ))}
        </div>

        {/* COMMERCIAUX */}
        <div style={styles.sectionLabel}>{t(lang, 'my_commerciaux')}</div>
        {commerciaux.length === 0 && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 32 }}>👥</div>
            <div style={{ fontSize: 14, color: '#888', marginTop: 8 }}>{t(lang, 'no_commercial')}</div>
          </div>
        )}
        {commerciaux.map((c, i) => {
          const debutMois = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
          const cvsAll = visites.filter(v => v.commercialId === c.id);
          const cvs = cvsAll.filter(v => v.heureDebut && new Date(v.heureDebut) >= debutMois);
          const cvsToday = cvsAll.filter(v => v.heureDebut && new Date(v.heureDebut).toDateString() === new Date().toDateString());
          const score = Math.round((cvs.length / Math.max(c.objectifVisites || 60, 1)) * 100);
          const clientsComm = clients.filter(cl => cl.commercialIds?.includes(c.id));
          const colors = [['#FCEBEB', '#C0392B'], ['#E6F1FB', '#185FA5'], ['#EAF3DE', '#3B6D11']];
          const [bg, fg] = colors[i % 3];
          return (
            <div key={i} style={styles.commCard} onClick={() => setSelected(selected === i ? null : i)}>
              <div style={styles.commHeader}>
                <div style={{ ...styles.commAv, background: bg, color: fg }}>
                  {(c.prenom || c.nom || 'C')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={styles.commName}>{c.prenom} {c.nom}</div>
                  <div style={styles.commSub}>{(c.wilayas || []).join(' · ') || t(lang, 'non_assignes')} · {clientsComm.length} {t(lang, 'clients')}</div>
                </div>
                <div style={{ textAlign: isRTL ? 'left' : 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: score >= 80 ? '#3B6D11' : score >= 50 ? '#854F0B' : '#C0392B' }}>{score}%</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{cvsToday.length} {t(lang, 'visite')}(s)</div>
                </div>
              </div>
              {selected === i && (
                <div style={styles.commDetail}>
                  {[
                    { label: t(lang, 'visites_mois'), val: `${cvs.length} / ${c.objectifVisites || 60}`, pct: score },
                    { label: t(lang, 'clients_assignes'), val: `${clientsComm.length}`, pct: 100 },
                  ].map((k, j) => (
                    <div key={j} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#666' }}>{k.label}</span>
                        <span style={{ fontWeight: 600 }}>{k.val}</span>
                      </div>
                      <div style={{ height: 4, background: '#f0f0f0', borderRadius: 99 }}>
                        <div style={{ height: 4, background: '#1C2833', borderRadius: 99, width: `${Math.min(k.pct, 100)}%` }}></div>
                      </div>
                    </div>
                  ))}
                  <div style={styles.commBtns}>
                    <button style={styles.commBtn} onClick={(e) => { e.stopPropagation(); setVue('dispatch'); }}>{t(lang, 'clients_assignes')}</button>
                    <button style={{ ...styles.commBtn, background: '#1C2833', color: '#fff', border: 'none' }} onClick={(e) => { e.stopPropagation(); setFicheCommercial(c); }}>{t(lang, 'details')}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* VISITES RÉCENTES */}
        {visites.length > 0 && (
          <>
            <div style={styles.sectionLabel}>{t(lang, 'recent_visites')}</div>
            {visites.slice(0, 5).map((v, i) => (
              <div key={i} style={styles.visiteRow}>
                <div style={styles.visiteDot}></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1C2833' }}>{v.clientNom}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{v.commercialNom} · {v.resultat}</div>
                </div>
                <div style={{ fontSize: 11, color: '#888' }}>{v.heureDebut ? new Date(v.heureDebut).toLocaleDateString(lang === 'ar' ? 'ar-DZ' : 'fr-FR') : ''}</div>
              </div>
            ))}
          </>
        )}
      </div>

      {showTheme && <SelecteurTheme onClose={() => setShowTheme(false)} />}

      <div style={styles.navbar}>
        {[
          { label: `📊 ${t(lang, 'pc_dashboard')}`, action: () => setVue('dashboard') },
          { label: `🗺️ ${t(lang, 'carte_title')}`, action: () => setVue('carte') },
          { label: `📈 ${t(lang, 'rapports')}`, action: () => setVue('rapports') },
        ].map((n, i) => (
          <div key={i} style={styles.navItem} onClick={n.action}>{n.label}</div>
        ))}
      </div>
    </div>
  );
}

const topbarBtn = { background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 32, height: 32, fontSize: 16, cursor: 'pointer', position: 'relative' };
const badgeStyle = { position: 'absolute', top: -3, right: -3, background: '#E74C3C', color: '#fff', fontSize: 9, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 };

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 },
  topbarName: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  logoutBtn: { background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '6px 10px', color: '#fff', cursor: 'pointer' },
  content: { flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 },
  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  statCard: { background: '#fff', borderRadius: 10, padding: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  statVal: { fontSize: 24, fontWeight: 700 },
  statLabel: { fontSize: 12, color: '#666', marginTop: 2 },
  statSub: { fontSize: 10, color: '#aaa', marginTop: 2 },
  alertCard: { background: '#FAEEDA', borderRadius: 10, padding: '12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', border: '1px solid #F0C060' },
  alertIcon: { fontSize: 20, flexShrink: 0 },
  alertTitle: { fontSize: 13, fontWeight: 600, color: '#854F0B' },
  alertSub: { fontSize: 11, color: '#854F0B', opacity: 0.8, marginTop: 2 },
  alertArrow: { fontSize: 16, color: '#854F0B' },
  sectionLabel: { fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 },
  actionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  actionCard: { background: '#fff', borderRadius: 10, padding: '14px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  actionIcon: { fontSize: 22 },
  actionLabel: { fontSize: 12, fontWeight: 500, color: '#1C2833', textAlign: 'center' },
  emptyState: { background: '#fff', borderRadius: 10, padding: '2rem', textAlign: 'center' },
  commCard: { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  commHeader: { padding: 12, display: 'flex', alignItems: 'center', gap: 10 },
  commAv: { width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 },
  commName: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  commSub: { fontSize: 11, color: '#888', marginTop: 2 },
  commDetail: { padding: 12, borderTop: '1px solid #f5f5f5', background: '#fafafa' },
  commBtns: { display: 'flex', gap: 6, marginTop: 10 },
  commBtn: { flex: 1, padding: 8, borderRadius: 8, border: '1px solid #eee', background: '#fff', color: '#666', fontSize: 11, cursor: 'pointer' },
  visiteRow: { display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 10, padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  visiteDot: { width: 8, height: 8, borderRadius: '50%', background: '#3B6D11', flexShrink: 0 },
  navbar: { display: 'flex', background: '#fff', borderTop: '1px solid #eee', padding: '8px 0 4px' },
  navItem: { flex: 1, textAlign: 'center', fontSize: 9, color: '#888', cursor: 'pointer', padding: '4px 0' },
};
